import React from "react";
import { observer } from "mobx-react";
import { Button } from "antd";
import Avatar from "@app/components/user/avatar";
import format from "@app/lib/format";
import { Activities as ActivityType, ActivityLogView, ReportDocumentStyle } from "@app/constants";
import BaseModal from "@app/components/modal";
import {
    Abstract,
    DocumentBookmark,
    ScoringCriteria,
    Answer,
    DefaultView,
    AdditionalData,
    Question,
    AdditionalDataConfiguration,
    Comment,
    Search,
    Tag,
    Keyword,
    Reason,
    Note,
    Highlight,
    ReportDocumentSection,
    ReportDocumentSections,
    ReportDocumentMetaData,
    Reference,
    ReviewStage,
} from "./details";

import "./style/list.scoped.scss";

const MoreDetails = observer(({ activity, moreDetails }) => {
    let Component = DefaultView;
    let displayFlow = "column";

    if (
        activity.path === "article.review.l2.questions" ||
        activity.path === "review.l2.questions"
    ) {
        Component = Answer;
    } else if (activity.path === "additionalData") {
        Component = AdditionalData;
    } else if (activity.target?.type && activity.target?.type.indexOf("comment") !== -1) {
        Component = Comment;
    } else if (["slr.l2.questions", "sota.l2.questions"].includes(activity.path)) {
        Component = Question;
    } else if (activity.path === "slr.l2.criteria" || activity.path === "sota.l2.criteria") {
        Component = ScoringCriteria;
    } else if (["slr.additionalData", "sota.additionalData"].includes(activity.path)) {
        Component = AdditionalDataConfiguration;
    } else if (activity.path === "abstract") {
        Component = Abstract;
    } else if (activity.target?.type === ActivityType.SEARCH) {
        Component = Search;
    } else if (
        ["contentTags", "documentTags", "articleTags"].includes(activity.path) &&
        activity.target?.type === ActivityType.PROJECT_CONFIG
    ) {
        Component = Tag;
    } else if (["slr.keywords", "sota.keywords"].includes(activity.path)) {
        Component = Keyword;
    } else if (
        [
            "slr.l2.excludeReasons",
            "slr.l1.excludeReasons",
            "sota.l2.excludeReasons",
            "sota.l1.excludeReasons",
        ].includes(activity.path)
    ) {
        Component = Reason;
    } else if (activity.path === "note" && activity.target?.type === ActivityType.ARTICLE) {
        Component = Note;
    } else if (activity.target?.type === ActivityType.ARTICLE_HIGHLIGHT) {
        if (activity.path !== "content") {
            Component = Highlight;
        }
    } else if (activity.target?.type === ActivityType.DOCUMENT_BOOKMARK) {
        Component = DocumentBookmark;
        delete moreDetails.title;
    } else if (activity.target?.type === ActivityType.REPORT_DOCUMENT) {
        displayFlow = "row";
        if (activity.keywords.includes("update-sections")) {
            Component = ReportDocumentSections;
        } else if (activity.path === "reviewConfig.stages") {
            Component = ReviewStage;
        } else if (activity.path === "variables") {
            Component = ReportDocumentMetaData;
        }
    } else if (activity.target?.type === ActivityType.REPORT_DOCUMENT_SECTION) {
        displayFlow = "row";
        Component = ReportDocumentSection;
    } else if (activity.target?.type === ActivityType.CITATION) {
        displayFlow = "row";
        Component = Reference;
    } else if (activity.path === "review.stage") {
        displayFlow = "row";
        Component = ReviewStage;
    }

    return (
        <div className={`container ${displayFlow}`}>
            {moreDetails.title && <div className="title">{moreDetails.title}</div>}
            <>
                {moreDetails.initial !== undefined && moreDetails.initial !== null && (
                    <div className="old">
                        <span className="type">Old Value</span>
                        <Component data={moreDetails.initial} activity={activity} />
                    </div>
                )}
                {moreDetails.edited !== undefined && moreDetails.edited !== null && (
                    <div className="new">
                        <span className="type">New Value</span>
                        <Component data={moreDetails.edited} activity={activity} />
                    </div>
                )}
            </>
        </div>
    );
});

const Modal = observer(({ show, hideModal, activity, moreDetails }) => {
    return (
        <BaseModal
            title={moreDetails?.heading ?? "Details"}
            visible={show}
            width={800}
            onCancel={hideModal}
            cancelText="Close"
            okButtonProps={{ style: { display: "none" } }}
            keyboard={true}
        >
            <MoreDetails activity={activity} moreDetails={moreDetails} />
        </BaseModal>
    );
});

const MoreDetailsButton = observer(({ setShow, setCurrent, activity, moreDetails, setDetails }) => {
    return (
        <Button
            onClick={() => {
                setShow(true);
                setCurrent(activity);
                setDetails(moreDetails);
            }}
        >
            More Details
        </Button>
    );
});

const List = observer(({ activities, date, context, view }) => {
    const [show, setShow] = React.useState(false);
    const [current, setCurrent] = React.useState(false);
    const [details, setDetails] = React.useState(false);
    const hideModal = () => setShow(false);

    // The process function is responsible for preparing the activity description.
    // It takes an activity and checks the path, type & operation and prepares a meaningful context.
    // "moreDetails" object is returned for certain actions. This enabled the More Details buttons.
    // moreDetails has two keys, edited & initial contains the details to show the Old and New Value.

    const process = (activity, context) => {
        const { operation, newValue, oldValue, path, target, action, keywords } = activity;

        let moreDetails = undefined;

        // Project Config
        if (target?.type === ActivityType.PROJECT_CONFIG) {
            if (path === "contentTags") {
                if (operation === "add") {
                    moreDetails = {
                        edited: newValue,
                        initial: undefined,
                        heading: "Content Tag",
                    };
                } else if (operation === "update") {
                    moreDetails = {
                        edited: newValue,
                        initial: oldValue,
                        heading: "Content Tag",
                    };
                } else if (operation === "remove") {
                    moreDetails = {
                        edited: undefined,
                        initial: oldValue,
                        heading: "Content Tag",
                    };
                }
            }
            // searchDb
            else if (path === "search.customDatabases") {
                if (operation === "update") {
                    moreDetails = {
                        initial: oldValue.name,
                        edited: newValue.name,
                        heading: "Custom Database ",
                    };
                }
            } else if (path === "slr.l2.excludeReasons") {
                if (operation === "update") {
                    moreDetails = {
                        edited: newValue,
                        initial: oldValue,
                        heading: "DUE L2 Exclusion Reason",
                    };
                } else if (operation === "remove") {
                    moreDetails = {
                        edited: newValue,
                        initial: oldValue,
                        heading: "DUE L2 Exclusion Reason",
                    };
                }
            } else if (path === "slr.l2.criteria") {
                moreDetails = {
                    edited: newValue,
                    initial: oldValue,
                    heading: "DUE Scoring Criteria",
                };
            } else if (path === "slr.additionalData") {
                moreDetails = {
                    edited: newValue,
                    initial: oldValue,
                    heading: "DUE Data Extraction Field",
                };
            } else if (path === "slr.l1.excludeReasons") {
                if (operation === "update") {
                    moreDetails = {
                        edited: newValue,
                        initial: oldValue,
                        heading: "DUE L1 Exclusion Reason",
                    };
                } else if (operation === "remove") {
                    moreDetails = {
                        edited: newValue,
                        initial: oldValue,
                        heading: "DUE L1 Exclusion Reason",
                    };
                }
            } else if (path === "slr.keywords") {
                if (operation === "update") {
                    moreDetails = {
                        edited: newValue,
                        initial: oldValue,
                        heading: "DUE Keyword",
                    };
                } else if (operation === "remove") {
                    moreDetails = {
                        edited: undefined,
                        initial: oldValue,
                        heading: "DUE Keyword",
                    };
                }
            } else if (path === "slr.l2.questions") {
                moreDetails = {
                    initial: oldValue,
                    edited: newValue,
                    heading: "DUE Question",
                };
            } else if (path === "sota.keywords") {
                if (operation === "update") {
                    moreDetails = {
                        edited: newValue,
                        initial: oldValue,
                        heading: "SoTA Keyword",
                    };
                } else if (operation === "remove") {
                    moreDetails = {
                        edited: undefined,
                        initial: oldValue,
                        heading: "SoTA Keyword",
                    };
                }
            } else if (path === "sota.l2.questions") {
                moreDetails = {
                    initial: oldValue,
                    edited: newValue,
                    heading: "SoTA Question",
                };
            } else if (path === "sota.l1.excludeReasons") {
                if (operation === "update") {
                    moreDetails = {
                        edited: newValue,
                        initial: oldValue,
                        heading: "SoTA L1 Exclusion Reason",
                    };
                } else if (operation === "remove") {
                    moreDetails = {
                        edited: newValue,
                        initial: oldValue,
                        heading: "SoTA L1 Exclusion Reason",
                    };
                }
            } else if (path === "sota.additionalData") {
                moreDetails = {
                    edited: newValue,
                    initial: oldValue,
                    heading: "SoTA Data Extraction Field",
                };
            } else if (path === "sota.l2.excludeReasons") {
                if (operation === "update") {
                    moreDetails = {
                        edited: newValue,
                        initial: oldValue,
                        heading: "SoTA L2 Exclusion Reason",
                    };
                } else if (operation === "remove") {
                    moreDetails = {
                        edited: newValue,
                        initial: oldValue,
                        heading: "SoTA L2 Exclusion Reason",
                    };
                }
            } else if (path === "sota.l2.criteria") {
                moreDetails = {
                    edited: newValue,
                    initial: oldValue,
                    heading: "SOTA Scoring Criteria",
                };
            } else if (path === "documentTags") {
                if (action === "update") {
                    if (operation === "add") {
                        moreDetails = {
                            edited: newValue,
                            initial: undefined,
                            heading: "Document Tag",
                        };
                    } else if (operation === "update") {
                        moreDetails = {
                            edited: newValue,
                            initial: oldValue,
                            heading: "Document Tag",
                        };
                    } else if (operation === "remove") {
                        moreDetails = {
                            edited: newValue,
                            initial: undefined,
                            heading: "Document Tag",
                        };
                    }
                }
            } else if (path === "articleTags") {
                if (operation === "add") {
                    moreDetails = {
                        edited: newValue,
                        initial: undefined,
                        heading: "Article Tag",
                    };
                } else if (operation === "update") {
                    moreDetails = {
                        edited: newValue,
                        initial: oldValue,
                        heading: "Article Tag",
                    };
                } else if (operation === "remove") {
                    moreDetails = {
                        edited: undefined,
                        initial: oldValue,
                        heading: "Article Tag",
                    };
                }
            } else if (path === "review.stages") {
                if (operation === "update") {
                    moreDetails = {
                        edited: newValue,
                        initial: oldValue,
                        heading: "Review Stage",
                    };
                }
            } else if (path === "reference.style") {
                if (operation === "update") {
                    moreDetails = {
                        edited: newValue,
                        initial: oldValue,
                        heading: "Reference Style",
                    };
                }
            } else if (/reportDocuments.style.+/.test(path)) {
                let heading, style;
                const attr = path.split(".")[2];

                if (attr !== "file") {
                    if (attr === "sectionNumbering") {
                        heading = "Section Numbering";
                        style = ReportDocumentStyle.SECTION_NUMBERING;
                    } else if (attr === "captionIndex") {
                        heading = "Caption Numbering";
                        style = ReportDocumentStyle.CAPTION_NUMBERING;
                    } else if (attr === "captionRef") {
                        heading = "Table / Figure Reference";
                        style = ReportDocumentStyle.CAPTION_REF;
                    } else if (attr === "sectionRef") {
                        heading = "Section Reference";
                        style = ReportDocumentStyle.SECTION_REF;
                    } else if (attr === "tocLevel") {
                        heading = "TOC Levels";
                        style = ReportDocumentStyle.TOC_LEVEL;
                    }

                    moreDetails = {
                        edited: style?.[newValue],
                        initial: style?.[oldValue],
                        heading,
                    };
                }
            } else if (path === "reports.slr.fields" || path === "reports.sota.fields") {
                if (operation === "update") {
                    const getValue = (value) => {
                        if (value.fieldType === "multi-select") {
                            return value.values?.map((v) => v.label).join(", ");
                        } else if (["showReviewQuestions"].includes(value.name)) {
                            if (value.value === "true") {
                                return value.values?.map((o) => o.label).join(", ");
                            }
                        }
                        return value.value;
                    };

                    moreDetails = {
                        edited: getValue(newValue),
                        initial: getValue(oldValue),
                        heading: newValue.label,
                    };
                }
            }
        }

        // Project Overview
        else if (target?.type === ActivityType.PROJECT) {
            if (path === "name") {
                const newVal = format.shorten(format.sanitize(newValue));
                const old = format.shorten(format.sanitize(oldValue));

                if (action === "update") {
                    moreDetails = {
                        edited: newVal,
                        initial: old,
                        heading: "Project Name",
                    };
                }
            } else if (path === "description") {
                const newVal = format.shorten(format.sanitize(newValue));
                const old = format.shorten(format.sanitize(oldValue));
                moreDetails = {
                    edited: newVal,
                    initial: old,
                    heading: "Project Description",
                };
            }
        }

        // Comments
        else if (
            [
                ActivityType.PROJECT_COMMENT,
                ActivityType.SEARCH_COMMENT,
                ActivityType.DICTIONARY_COMMENT,
                ActivityType.ABBREVIATION_COMMENT,
                ActivityType.REFERENCE_COMMENT,
            ].includes(target?.type)
        ) {
            let heading = target.type
                .split("_")
                .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                .join(" ");

            moreDetails = {
                edited: newValue,
                initial: oldValue,
                heading,
            };
        }

        // Search
        else if (target?.type === ActivityType.SEARCH) {
            if (action === "create") {
                moreDetails = {
                    edited: newValue,
                    heading: "Search",
                };
            } else if (action === "update") {
                moreDetails = {
                    edited: newValue,
                    initial: oldValue,
                    heading: "Search",
                };
            } else if (action === "save" && newValue) {
                moreDetails = {
                    edited: newValue,
                    heading: "Search",
                };
            }
        }

        // Article
        else if (target?.type === ActivityType.ARTICLE) {
            if (keywords?.includes("article-galaxy")) {
                if (keywords.includes("order")) {
                    const orderId = format.sanitize(activity?.data?.orderId);
                    const rndId = format.sanitize(activity?.data?.rndId);
                    const orderMsg = format.shorten(format.sanitize(activity?.data?.message));

                    moreDetails = {
                        edited: { orderId, rndId, message: orderMsg },
                        heading: "ArticleGalaxy Order",
                    };
                } else if (keywords.includes("download")) {
                    const filename = format.shorten(format.sanitize(activity?.data?.filename));
                    const size = activity?.data?.size;
                    const url = format.sanitize(activity?.data?.url);

                    moreDetails = {
                        edited: { filename, size, url },
                        heading: "ArticleGalaxy Download",
                    };
                }
            }
            if (path === "note") {
                moreDetails = {
                    edited: newValue,
                    initial: oldValue,
                    heading: "Notes",
                };

                if (operation === "add") {
                    // First time adding a note to the article
                    moreDetails.initial = undefined;
                } else if (operation === "update") {
                    if (newValue === "") {
                        moreDetails.edited = undefined;
                    } else if (oldValue === "") {
                        moreDetails.initial = undefined;
                        // Previously there was an old note with value as "".
                    }
                }
            } else if (action === "exclude") {
                // L1 Or L2 Exclude

                const stage = newValue?.stage === "l1" ? " L1 Screening" : "L2 Review";
                moreDetails = {
                    edited: `Excluded at ${stage} ${format.sanitize(newValue?.reason)}`,
                    initial: oldValue?.reason,
                    heading: "Exclusion Reasons",
                };
            } else if (path === "articleTags") {
                const old = format.shorten(format.sanitize(oldValue?.label));
                const newVal = format.shorten(format.sanitize(newValue?.label));

                if (operation === "remove") {
                    moreDetails = {
                        edited: newVal,
                        initial: old,
                        heading: "Tag",
                    };
                } else if (operation === "add") {
                    moreDetails = {
                        edited: newVal,
                        initial: old,
                        heading: "Tag",
                    };
                } else if (operation === "update") {
                    moreDetails = {
                        edited: newVal,
                        initial: old,
                        heading: "Tag",
                    };
                }
            } else if (path === "article.review.l2.questions" || path === "review.l2.questions") {
                if (operation === "update" && newValue && oldValue) {
                    moreDetails = {
                        edited: newValue,
                        initial: oldValue,
                        heading: "Answer",
                    };

                    if (!oldValue?.answer?.answer) {
                        moreDetails.initial = undefined;
                    }
                }
            } else if (path === "abstract") {
                if (newValue === "") {
                    moreDetails = {
                        edited: newValue,
                        initial: oldValue,
                        heading: "Abstract",
                    };
                } else {
                    moreDetails = {
                        edited: newValue,
                        initial: oldValue,
                        heading: "Abstract",
                    };
                }
            } else if (path === "authors") {
                const newName = `${format.sanitize(newValue?.firstName)} ${format.sanitize(
                    newValue?.lastName,
                )}`;
                const oldName = `${format.sanitize(oldValue?.firstName)} ${format.sanitize(
                    oldValue?.lastName,
                )}`;

                if (operation === "update") {
                    moreDetails = {
                        edited: newName,
                        initial: oldName,
                        heading: "Author",
                    };
                }
            } else if (path === "devices") {
                const newVal = format.sanitize(newValue);
                if (operation === "remove") {
                    moreDetails = {
                        edited: newVal,
                        initial: undefined,
                        heading: "Device",
                    };
                }
            } else if (path === "descriptors") {
                if (operation === "remove") {
                    moreDetails = {
                        edited: undefined,
                        initial: oldValue,
                        heading: "Descriptor",
                    };
                }
            } else if (path === "link") {
                const old = format.shorten(format.sanitize(oldValue));
                const newVal = format.shorten(format.sanitize(newValue));
                if (newValue === "") {
                    moreDetails = {
                        edited: undefined,
                        initial: old,
                        heading: "Link",
                    };
                } else {
                    moreDetails = {
                        edited: newVal,
                        initial: old,
                        heading: "Link",
                    };
                }
            } else if (path === "keywords") {
                const old = format.shorten(format.sanitize(oldValue));
                if (operation === "remove") {
                    moreDetails = {
                        edited: undefined,
                        initial: old,
                        heading: "Author Keyword",
                    };
                }
            } else if (path === "title") {
                if (operation === "update") {
                    moreDetails = {
                        edited: newValue,
                        initial: oldValue,
                        heading: "Title",
                    };
                }
            } else if (path === "ids.doi") {
                if (operation === "update") {
                    if (newValue !== "" && oldValue !== "") {
                        moreDetails = {
                            heading: "DOI",
                            edited: newValue,
                            initial: oldValue,
                        };
                    }
                }
            } else if (path === "ids.pubmed") {
                if (operation === "update") {
                    if (newValue !== "" && oldValue !== "") {
                        moreDetails = {
                            heading: "PubMed",
                            edited: newValue,
                            initial: oldValue,
                        };
                    }
                }
            } else if (path === "additionalData") {
                if (operation === "add") {
                    moreDetails = {
                        edited: newValue,
                        initial: undefined,
                        heading: "Data Extraction Field",
                    };
                } else if (operation === "update") {
                    let edited = newValue;
                    let initial = oldValue;

                    if (keywords.includes("handled-suggestion")) {
                        initial = undefined;
                    }

                    moreDetails = {
                        edited,
                        initial,
                        heading: "Data Extraction Field",
                    };
                } else if (operation === "remove") {
                    moreDetails = {
                        edited: newValue,
                        initial: oldValue,
                        heading: "Data Extraction Field",
                    };
                }
            }
        } else if (target.type === ActivityType.ARTICLE_COMMENT) {
            moreDetails = {
                edited: newValue,
                initial: oldValue,
                heading: "Article Comment",
            };
        } else if (target?.type === ActivityType.ARTICLE_HIGHLIGHT) {
            if (action === "create") {
                moreDetails = {
                    edited: newValue,
                    initial: oldValue,
                    heading: "Article Highlight",
                };
            } else if (action === "delete") {
                moreDetails = {
                    edited: undefined,
                    initial: oldValue,
                    heading: "Article Highlight",
                };
            } else if (action === "update") {
                moreDetails = {
                    edited: newValue,
                    initial: oldValue,
                    heading: "Article Highlight",
                };
            }
        }

        // Document
        else if (target?.type === ActivityType.DOCUMENT) {
            if (path === "documentTags") {
                const newVal = format.shorten(format.sanitize(newValue?.label));
                const old = format.shorten(format.sanitize(oldValue?.label));

                if (action === "update") {
                    if (operation === "update") {
                        moreDetails = {
                            edited: newVal,
                            initial: old,
                            heading: "Tag",
                        };
                    } else if (operation === "remove") {
                        moreDetails = {
                            edited: newVal,
                            initial: old,
                            heading: "Tag",
                        };
                    }
                }
            } else if (path === "name") {
                if (action === "update") {
                    moreDetails = {
                        edited: newValue,
                        initial: oldValue,
                        heading: "Document Name",
                    };
                }
            }
        } else if (target?.type === ActivityType.DOCUMENT_COMMENT) {
            moreDetails = {
                edited: newValue,
                initial: oldValue,
                heading: "Document Comment",
            };
        } else if (target?.type === ActivityType.DOCUMENT_BOOKMARK) {
            const heading = "Bookmark";

            if (action === "create") {
                moreDetails = {
                    edited: newValue,
                    heading,
                };
            } else if (action === "update") {
                moreDetails = {
                    initial: oldValue,
                    edited: newValue,
                    heading,
                };
            }
        } else if (target?.type === ActivityType.DOCUMENT_CATEGORY) {
            const newVal = format.shorten(format.sanitize(target?.name));
            if (path === "name") {
                if (action === "update") {
                    moreDetails = {
                        edited: newVal,
                        initial: oldValue,
                        heading: "Document Category",
                    };
                }
            }
        }

        // Report Document
        else if (target?.type === ActivityType.REPORT_DOCUMENT) {
            if (action === "update") {
                if (keywords.includes("update-sections")) {
                    moreDetails = {
                        edited: newValue,
                        initial: oldValue,
                        heading: "Report Document Sections",
                    };
                } else if (path === "reviewConfig.stages" || path === "review.stage") {
                    if (operation === "update") {
                        moreDetails = {
                            edited: newValue,
                            initial: oldValue,
                            heading: "Review Stage",
                        };
                    }
                } else if (path === "review.stage.reviewers") {
                    if (operation === "update") {
                        moreDetails = {
                            edited: newValue?.fullName + " reviewed: " + newValue?.reviewed,
                            initial: oldValue?.fullName + " reviewed: " + oldValue?.reviewed,
                            heading: "Review Stage Reviewers",
                        };
                    }
                } else if (path) {
                    const property = path
                        .split(/[.]|(?=[A-Z])/)
                        .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
                        .join(" ");
                    moreDetails = {
                        edited: newValue,
                        initial: oldValue,
                        heading: property,
                    };
                }
            }
        } else if (target?.type === ActivityType.REPORT_DOCUMENT_SECTION) {
            if (action === "update") {
                moreDetails = {
                    edited: newValue,
                    initial: oldValue,
                    heading: "Report Document Section",
                };
            }
        } else if (target?.type === ActivityType.DICTIONARY) {
            if (action === "update") {
                moreDetails = {
                    edited: newValue,
                    initial: oldValue,
                    heading: "Dictionary",
                };
            }
        } else if (target?.type === ActivityType.ABBREVIATION) {
            if (action === "update") {
                moreDetails = {
                    edited: newValue,
                    initial: oldValue,
                    heading: "Abbreviation",
                };
            }
        } else if (target?.type === ActivityType.CITATION) {
            if (action === "update") {
                moreDetails = {
                    edited: newValue,
                    initial: oldValue,
                    heading: "Reference",
                };
            }
        } else if (target?.type === ActivityType.REPORT_DOCUMENT_COMMENT) {
            if (action === "update") {
                moreDetails = {
                    edited: newValue,
                    initial: oldValue,
                    heading: "Report Document Comment",
                };
            }
        }
        if (target?.type === ActivityType.ADVERSE_EVENTS) {
            let heading = "",
                title,
                editedValue = newValue,
                initialValue = oldValue;

            if (keywords.includes("tplc")) {
                heading = "TPLC";
            } else if (keywords.includes("patient problems")) {
                heading = "Patient Problems";
            }

            switch (path) {
                case "startDate":
                    title = "From Date";
                    editedValue = format.date(editedValue);
                    initialValue = format.date(initialValue);
                    break;
                case "endDate":
                    title = "To Date";
                    editedValue = format.date(editedValue);
                    initialValue = format.date(initialValue);
                    break;
                case "cutOff":
                    title = "Results cut-off";
                    break;
                case "deviceNames":
                    title = "Brands";
                    break;
                case "manufacturerNames":
                    title = "Manufacturers";
                    break;
                default:
                    break;
            }

            moreDetails = {
                edited: `${editedValue ?? ""}`,
                initial: `${initialValue ?? ""}`,
                heading,
                title,
            };
        }
        if (context !== ActivityType.Article && activity.article?._id) {
            if (moreDetails) {
                moreDetails.title =
                    activity?.article?.key + " - " + format.sanitize(activity.article?.name);
            }
        }
        if (context !== ActivityType.Document && activity.document?._id) {
            if (moreDetails) {
                moreDetails.title = activity.document?.name;
            }
        }

        const description = activity.title;
        return {
            moreDetails: moreDetails ?? undefined,
            description,
        };
    };

    const ProjectActivity = observer(({ activity }) => {
        const { description, moreDetails } = process(activity, context);

        return (
            <div className="details">
                <div className="data">
                    <span className="date-time">{format.datetime(activity.time)}</span>
                    <span
                        className="title"
                        dangerouslySetInnerHTML={{
                            __html: description,
                        }}
                    />
                    <div className="user">
                        <Avatar className="avatar" user={activity.user} size={60} />
                        <span className="name">{activity.user.fullName} </span>
                    </div>
                </div>
                <div className="footer">
                    {moreDetails && (
                        <MoreDetailsButton
                            setShow={setShow}
                            setCurrent={setCurrent}
                            activity={activity}
                            moreDetails={moreDetails}
                            setDetails={setDetails}
                        />
                    )}
                </div>
            </div>
        );
    });

    const Activities = observer(() => {
        return (
            <div className="activity-list">
                <div className="date">{date}</div>

                {activities.map((activity) => {
                    const { description, moreDetails } = process(activity, context);
                    return (
                        <div className="side-content" key={activity._id}>
                            <div className="details">
                                <span
                                    className="title"
                                    dangerouslySetInnerHTML={{
                                        __html: description,
                                    }}
                                />
                                <span className="time">{activity.created}</span>
                            </div>
                            <div className="footer">
                                <div className="user">
                                    <Avatar className="avatar" user={activity.user} size={60} />
                                    <span className="name">{activity.user.fullName} </span>
                                </div>
                                {moreDetails && (
                                    <MoreDetailsButton
                                        setShow={setShow}
                                        setCurrent={setCurrent}
                                        activity={activity}
                                        moreDetails={moreDetails}
                                        setDetails={setDetails}
                                    />
                                )}
                            </div>
                        </div>
                    );
                })}

                {show && (
                    <Modal
                        show={show}
                        hideModal={hideModal}
                        activity={current}
                        moreDetails={details}
                    />
                )}
            </div>
        );
    });

    if (view === ActivityLogView.Main) {
        return (
            <div className="project-activity">
                {activities.map((activity) => {
                    return <ProjectActivity activity={activity} key={activity._id} />;
                })}
                {show && (
                    <Modal
                        show={show}
                        hideModal={hideModal}
                        activity={current}
                        moreDetails={details}
                    />
                )}
            </div>
        );
    }

    if (view === ActivityLogView.Sidebar) {
        return <Activities />;
    }
});

export default List;
